// Service worker registration and the update handshake.
//
// The update model is deliberately NOT "skipWaiting on install". A worker that
// activates under a running app swaps the asset set beneath a mounted React
// tree, and the next lazy route import 404s — which is exactly the failure
// `installChunkReloadGuard()` exists to recover from. Causing that on purpose,
// then recovering from it, is worse than asking.
//
// So: the new worker installs and WAITS. We tell the app. The app tells the
// user. The user decides, and only then do we post SKIP_WAITING and reload.

export type UpdateListener = () => void;

interface RegisterResult {
  registration: ServiceWorkerRegistration | null;
  /** Present when a new worker is installed and waiting to take over. */
  waiting: ServiceWorker | null;
}

let current: ServiceWorkerRegistration | null = null;
let waitingWorker: ServiceWorker | null = null;
const listeners = new Set<UpdateListener>();

export function onUpdateAvailable(fn: UpdateListener): () => void {
  listeners.add(fn);
  // Fire immediately if the update landed before the subscriber mounted.
  if (waitingWorker) fn();
  return () => listeners.delete(fn);
}

function announce(worker: ServiceWorker): void {
  waitingWorker = worker;
  listeners.forEach((fn) => fn());
}

export async function registerServiceWorker(
  url = "/sw.js",
): Promise<RegisterResult> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return { registration: null, waiting: null };
  }
  // Dev runs from source with no built asset hashes; a worker caching Vite's
  // unhashed dev modules produces confusing staleness for zero benefit.
  if (import.meta.env.DEV && !import.meta.env.VITE_SW_IN_DEV) {
    return { registration: null, waiting: null };
  }

  try {
    const registration = await navigator.serviceWorker.register(url, {
      scope: "/",
      // Without this the browser may serve sw.js itself from HTTP cache, and a
      // bad worker becomes unreplaceable for as long as that cache lives.
      updateViaCache: "none",
    });
    current = registration;

    if (registration.waiting && navigator.serviceWorker.controller) {
      announce(registration.waiting);
    }

    registration.addEventListener("updatefound", () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        // `controller` is null on the very first install — that is not an
        // update, it is the app coming under management for the first time,
        // and prompting there would be nonsense.
        if (
          installing.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          announce(installing);
        }
      });
    });

    return { registration, waiting: registration.waiting };
  } catch {
    // A failed registration must never block boot. The app works fine
    // uninstalled; it just loses offline.
    return { registration: null, waiting: null };
  }
}

/** Accept a waiting update: swap the worker, then reload exactly once. */
export function applyUpdate(): void {
  const worker = waitingWorker ?? current?.waiting ?? null;
  if (!worker) return;
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  worker.postMessage("SKIP_WAITING");
}

/** Ask the browser to re-check for a new worker (e.g. on tab focus). */
export async function checkForUpdate(): Promise<void> {
  try {
    await current?.update();
  } catch {
    /* offline, or the check raced a reload — neither is actionable */
  }
}
